import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Shield, ShieldAlert, Loader2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";

const SupportAccessToggle = () => {
  const { user } = useAuth();
  const [isGranted, setIsGranted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    const fetchGrant = async () => {
      const { data } = await (supabase as any)
        .from("support_access_grants")
        .select("is_active")
        .eq("user_id", user.id)
        .maybeSingle();
      setIsGranted((data as any)?.is_active === true);
      setIsLoading(false);
    };
    fetchGrant();
  }, [user]);

  const handleToggle = async (checked: boolean) => {
    if (!user) return;
    setIsSaving(true);

    try {
      const { error } = await (supabase.rpc as any)("set_support_access", { p_active: checked });
      if (error) throw error;
      setIsGranted(checked);
      toast({
        title: checked ? "Support access granted" : "Support access revoked",
        description: checked
          ? "Our support team can now view your proposals and contracts to assist you."
          : "Our support team can no longer view your data.",
      });
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Failed to update support access.", variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Card className="border-border bg-card">
        <CardContent className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border bg-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-foreground">
          <Shield className="h-5 w-5 text-primary" /> Support Access
        </CardTitle>
        <CardDescription>
          Control whether our support team can view your proposals, contracts, and threat assessments to help resolve issues.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between p-4 rounded-lg border border-border bg-secondary/30">
          <div className="flex items-center gap-3">
            {isGranted ? (
              <ShieldAlert className="h-5 w-5 text-warning" />
            ) : (
              <Shield className="h-5 w-5 text-success" />
            )}
            <div>
              <Label htmlFor="support-access" className="text-foreground font-medium cursor-pointer">
                {isGranted ? "Support access is enabled" : "Support access is disabled"}
              </Label>
              <p className="text-sm text-muted-foreground">
                {isGranted
                  ? "Support managers can currently view your proposals, contracts, and threat assessments."
                  : "No one outside your account can access your data."}
              </p>
            </div>
          </div>
          <Switch
            id="support-access"
            checked={isGranted}
            onCheckedChange={handleToggle}
            disabled={isSaving}
          />
        </div>

        <div className="rounded-lg border border-border p-4 bg-secondary/30">
          <h3 className="font-medium text-foreground mb-2">What support can access when enabled</h3>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>📄 Your proposals (read-only)</li>
            <li>📋 Your contracts (read-only)</li>
            <li>🔍 Your threat assessments (read-only)</li>
          </ul>
        </div>

        <div className="rounded-lg border border-border p-4 bg-secondary/30">
          <h3 className="font-medium text-foreground mb-2">What support can never access</h3>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>🔒 Your pricing and hourly rates</li>
            <li>🔒 Your contract templates</li>
            <li>🔒 Your company settings</li>
            <li>🔒 Your client contact information</li>
          </ul>
          <p className="text-xs text-muted-foreground mt-2">
            You can revoke access at any time. Access is immediately removed when disabled.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default SupportAccessToggle;
